import { motion } from "framer-motion";
import {
    ArrowRight,
    Briefcase,
    ClipboardEdit,
    Cloud,
    Eye,
    Loader2,
    MapPin,
    RefreshCw,
    Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { hasRole } from "../lib/roles";

const STATUS_STYLES = {
    "Publiée": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Brouillon": "bg-amber-50 text-amber-800 border-amber-200",
    "Archivée": "bg-slate-100 text-slate-600 border-slate-200",
    "Programmée": "bg-blue-50 text-blue-700 border-blue-200",
};

const STATUS_TABS = [
    { key: "", label: "Toutes" },
    { key: "Publiée", label: "Publiées" },
    { key: "Brouillon", label: "Brouillons" },
    { key: "Archivée", label: "Archivées" },
];

function formatDate(d) {
    if (!d) return "—";
    try {
        return new Intl.DateTimeFormat("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(new Date(d.replace(" ", "T")));
    } catch {
        return d;
    }
}

export default function Offres() {
    const { user } = useAuth();
    const isAdmin = hasRole(user, "admin");
    const [offers, setOffers] = useState([]);
    const [trames, setTrames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("Publiée");
    const [refreshing, setRefreshing] = useState(false);
    const [syncingId, setSyncingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = filter ? { status: filter } : {};
        const [{ data: ofs }, { data: tr }] = await Promise.all([
            api.get("/integrations/layan/offers", { params }),
            api.get("/entretien-trames").catch(() => ({ data: [] })),
        ]);
        setOffers(ofs);
        setTrames(tr);
        setLoading(false);
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    async function assignTrame(layanOfferId, trameId) {
        try {
            await api.patch(`/integrations/layan/offers/${layanOfferId}/trame`, {
                trame_id: trameId || null,
            });
            setOffers((prev) =>
                prev.map((o) => (o.layan_offer_id === layanOfferId ? { ...o, trame_id: trameId || null } : o)),
            );
            const trameName = trameId ? trames.find((t) => t.id === trameId)?.name : "trame par défaut";
            toast.success(`Trame assignée : ${trameName}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur d'assignation");
        }
    }

    async function refreshOffers() {
        setRefreshing(true);
        try {
            const { data } = await api.post("/integrations/layan/offers/sync");
            toast.success(`${data.offers_synced} offres rafraîchies`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setRefreshing(false);
        }
    }

    async function syncOffer(layanOfferId, title) {
        setSyncingId(layanOfferId);
        toast.info(`Sync candidats de "${title?.slice(0, 40)}…"`);
        try {
            const { data } = await api.post(
                `/integrations/layan/offers/${layanOfferId}/sync?limit=100&score_with_ai=false`,
            );
            toast.success(`${data.created} candidats importés en ${data.duration_s}s`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur de sync");
        } finally {
            setSyncingId(null);
        }
    }

    /**
     * iter99 · Suppression manuelle d'une offre archivée (admin only).
     * Le backend garde-fou empêche la suppression si des candidats sont
     * en pipeline actif (immersion / validé / onboarding / hired) ou si
     * un contrat non-void est rattaché.
     */
    async function deleteOffer(layanOfferId, title) {
        const shortTitle = title?.slice(0, 60) || `#${layanOfferId}`;
        if (!window.confirm(
            `Supprimer définitivement l'offre "${shortTitle}" ?\n\n`
            + "Les candidats liés (refusés, non appelés, terminés) seront préservés mais\n"
            + "leur lien vers cette offre sera cassé. Les candidats en pipeline actif\n"
            + "(immersion / validé / onboarding / embauché) bloquent la suppression."
        )) return;
        try {
            const { data } = await api.delete(
                `/integrations/layan/offers/${layanOfferId}`,
            );
            toast.success(
                `Offre supprimée · ${data.detached_candidates} candidat(s) détaché(s)`,
            );
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de supprimer");
        }
    }

    return (
        <div className="space-y-6" data-testid="offres-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                        <Cloud className="h-3 w-3" /> Source Layan
                    </div>
                    <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Offres
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        {offers.length} offre{offers.length > 1 ? "s" : ""} {filter ? `(${filter})` : "au total"} · {offers.reduce((sum, o) => sum + (o.applicants_total || 0), 0)} candidatures Layan reliées
                    </p>
                </div>
                <button
                    onClick={refreshOffers}
                    disabled={refreshing}
                    data-testid="refresh-offers"
                    className="btn-secondary"
                >
                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                    Rafraîchir depuis Layan
                </button>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="offre-filters">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t.key || "all"}
                        data-testid={`offre-filter-${t.key || "all"}`}
                        onClick={() => setFilter(t.key)}
                        className={`px-4 h-9 rounded-full text-xs font-bold border transition whitespace-nowrap ${
                            filter === t.key
                                ? "bg-shariti-orange text-white border-shariti-orange"
                                : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="card-soft p-12 flex justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-shariti-orange" />
                </div>
            ) : offers.length === 0 ? (
                <div className="card-soft p-12 text-center font-bold text-slate-400">
                    Aucune offre dans ce filtre.
                </div>
            ) : (
                <div className="grid gap-3">
                    {offers.map((o, i) => {
                        const tone = STATUS_STYLES[o.status] || STATUS_STYLES["Archivée"];
                        const isSyncing = syncingId === o.layan_offer_id;
                        return (
                            <motion.div
                                key={o.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.025 }}
                                data-testid={`offre-${o.layan_offer_id}`}
                                className="card-soft p-5 md:p-6"
                            >
                                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${tone}`}>
                                                {o.status}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                #{o.layan_offer_id}
                                            </span>
                                        </div>
                                        <h3 className="mt-2 text-base md:text-lg font-extrabold text-shariti-black truncate">
                                            {o.title}
                                        </h3>
                                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-slate-500">
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin className="h-3.5 w-3.5" /> {o.city || "—"} {o.department ? `(${o.department})` : ""}
                                                {o.region && (
                                                    <span
                                                        className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange text-[10px] font-black uppercase tracking-widest border border-shariti-orange/30"
                                                        data-testid={`offre-region-${o.layan_offer_id}`}
                                                    >
                                                        {o.region}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <Briefcase className="h-3.5 w-3.5" /> {o.job_category || "—"}
                                            </span>
                                            <span>Publiée le {formatDate(o.date_add)}</span>
                                            {o.number_recruitments ? (
                                                <span>· {o.number_recruitments} postes</span>
                                            ) : null}
                                        </div>
                                        {/* Trame assignment */}
                                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                <ClipboardEdit className="h-3 w-3" /> Trame d'entretien
                                            </span>
                                            {isAdmin && trames.length > 0 ? (
                                                <select
                                                    value={o.trame_id || ""}
                                                    onChange={(e) => assignTrame(o.layan_offer_id, e.target.value)}
                                                    data-testid={`offre-trame-select-${o.layan_offer_id}`}
                                                    className="bg-white border border-shariti-gray rounded-full px-3 h-7 text-[11px] font-bold text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10 cursor-pointer"
                                                >
                                                    <option value="">— Trame par défaut —</option>
                                                    {trames.map((t) => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.name} {t.is_default ? "(défaut)" : ""}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="text-[11px] font-bold text-shariti-orange">
                                                    {trames.find((t) => t.id === o.trame_id)?.name || "Trame par défaut"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center md:flex-col md:items-end gap-3 md:gap-2 shrink-0">
                                        <div className="flex items-center gap-4">
                                            <Stat label="Layan" value={o.applicants_total} accent="dark" />
                                            <Stat label="Importés" value={o.imported_count} accent="orange" />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={() => syncOffer(o.layan_offer_id, o.title)}
                                            disabled={isSyncing || o.applicants_total === 0}
                                            data-testid={`sync-offre-${o.layan_offer_id}`}
                                            className="btn-shariti disabled:opacity-50"
                                            title="Synchroniser les candidats de cette offre"
                                        >
                                            {isSyncing ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <RefreshCw className="h-4 w-4" />
                                            )}
                                            <span className="hidden md:inline">Sync</span>
                                        </button>
                                        <Link
                                            to={`/candidats?offer_id=${o.layan_offer_id}`}
                                            data-testid={`view-candidates-${o.layan_offer_id}`}
                                            className="btn-secondary"
                                        >
                                            <Eye className="h-4 w-4" />
                                            <span className="hidden md:inline">Candidats</span>
                                            <ArrowRight className="h-3.5 w-3.5 hidden md:inline" />
                                        </Link>
                                        {/* iter99 · Suppression manuelle d'une offre archivée (admin seul). */}
                                        {isAdmin && o.status === "Archivée" && (
                                            <button
                                                onClick={() => deleteOffer(o.layan_offer_id, o.title)}
                                                data-testid={`delete-offre-${o.layan_offer_id}`}
                                                title="Supprimer définitivement cette offre archivée"
                                                className="h-9 w-9 md:h-10 md:w-auto md:px-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-red-50 text-shariti-red hover:bg-red-100 border border-red-200 font-bold text-xs transition"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                <span className="hidden md:inline">Supprimer</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className="text-center">
            <div
                className={`text-xl md:text-2xl font-black tracking-tight ${
                    accent === "orange" ? "text-shariti-orange" : "text-shariti-black"
                }`}
            >
                {value ?? 0}
            </div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">
                {label}
            </div>
        </div>
    );
}
